import { Injectable } from '@angular/core';

import { NOT_APPLICABLE, TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { MarqueeType } from '@frontend/sports/types/components/content';

import { UrlHelperService } from '../navigation-core/url-helper.service';
import { HighlightsMarqueePage } from './highlights-marquee-page.enum';
import { HighlightsMarqueePageService } from './highlights-marquee-page.service';
import { MarqueeHelperService } from './marquee-helper.service';
import { FixtureTile, MarqueeTrackingModel, Tile, TrackingTileNames } from './marquee-tile.model';

@Injectable({ providedIn: 'root' })
export class MarqueeTrackingService {
    constructor(
        private trackingService: TrackingService,
        private urlHelper: UrlHelperService,
        private highlightsMarqueePageService: HighlightsMarqueePageService,
        private marqueeHelperService: MarqueeHelperService,
    ) {}

    trackEventClicks(
        index: number,
        tile: FixtureTile,
        page?: HighlightsMarqueePage,
        isCardAreaClicked: boolean = true,
        trackData?: MarqueeTrackingModel,
    ): void {
        const trafficId = tile.trafficId ? `|${tile.trafficId}` : '';
        const pageSource = page ? this.highlightsMarqueePageService.mapPageSource(page) : NOT_APPLICABLE;
        const marqueeTypeLabelName = this.getLabelEvent(tile.marqueeType);

        const trackingData = {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: 'Marquee',
            [trackingConstants.COMPONENT_LABEL_EVENT]: `${marqueeTypeLabelName}${trafficId}`,
            [trackingConstants.COMPONENT_ACTION_EVENT]: 'Marquee - Clicked',
            [trackingConstants.COMPONENT_POSITION_EVENT]: isCardAreaClicked ? 'Card Area Click' : 'See More Click',
            [trackingConstants.COMPONENT_LOCATION_EVENT]: pageSource,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: trackData ? trackData.eventDetails : this.getEventDetails(tile?.imageUrl, isCardAreaClicked),
            [trackingConstants.COMPONENT_URL_CLICKED]: trackData
                ? trackData.url
                : tile.event
                  ? this.urlHelper.getEventUrl(tile.event)
                  : tile.link!.href,
            [trackingConstants.SPORT_ID]: trackData ? (trackData.sportId ?? NOT_APPLICABLE) : tile.event ? `${tile.event.sport.id}` : NOT_APPLICABLE,
            [trackingConstants.SPORT_LEAGUE_ID]: trackData
                ? (trackData.leagueId ?? NOT_APPLICABLE)
                : tile.event
                  ? `${tile.event.league.id}`
                  : NOT_APPLICABLE,
            [trackingConstants.COMPONENT_CONTENT_POSITION]: index?.toString() ?? NOT_APPLICABLE,
            [trackingConstants.MARQUEE_NAME]: `${tile.trackingDetail?.marqueeName}` || NOT_APPLICABLE,
            [trackingConstants.MARQUEE_TYPE]: `${tile.source?.marqueeType}` || NOT_APPLICABLE,
            [trackingConstants.SITECORE_TEMPLATE_ID]: `${tile.trackingDetail?.sitecoreTemplateId}` || NOT_APPLICABLE,
            [trackingConstants.MARQUEE_CONTENT_LOGIC]: tile.source?.isAutomatedMarquee
                ? 'default - automated'
                : this.marqueeHelperService.fallbackFlagCollection[tile.fixtureId + tile.marketType]
                  ? 'fallback'
                  : 'default',
        };

        this.trackingService.track(trackingConstants.EVENT_CLICKS, trackingData);
    }

    trackShowMarquees(tiles: Tile[]): void {
        const marqueeTrackingData = tiles.map((tile, index) => {
            return {
                [trackingConstants.COMPONENT_CATEGORY_EVENT]: 'Marquee',
                [trackingConstants.COMPONENT_LABEL_EVENT]: `eventMarquee`,
                [trackingConstants.COMPONENT_ACTION_EVENT]: 'load',
                [trackingConstants.COMPONENT_POSITION_EVENT]: NOT_APPLICABLE,
                [trackingConstants.COMPONENT_LOCATION_EVENT]: NOT_APPLICABLE,
                [trackingConstants.COMPONENT_EVENT_DETAILS]: 'No Image Promoted',
                [trackingConstants.COMPONENT_URL_CLICKED]: tile.event ? this.urlHelper.getEventUrl(tile.event) : 'no href',
                [trackingConstants.SPORT_ID]: tile.event ? `${tile.event.sport.id}` : NOT_APPLICABLE,
                [trackingConstants.SPORT_LEAGUE_ID]: tile.event ? `${tile.event.league.id}` : NOT_APPLICABLE,
                [trackingConstants.COMPONENT_CONTENT_POSITION]: index?.toString() ?? NOT_APPLICABLE,
                [trackingConstants.MARQUEE_CONTENT_LOGIC]: tile.source?.isAutomatedMarquee
                    ? 'default - automated'
                    : this.marqueeHelperService.fallbackFlagCollection[tile.fixtureId + tile.marketType]
                      ? 'fallback'
                      : 'default',
                [trackingConstants.MARQUEE_NAME]: tile.trackingDetail?.marqueeName || NOT_APPLICABLE,
                [trackingConstants.MARQUEE_TYPE]: tile.source?.marqueeType || NOT_APPLICABLE,
                [trackingConstants.SITECORE_TEMPLATE_ID]: tile.trackingDetail?.sitecoreTemplateId || NOT_APPLICABLE,
                [trackingConstants.COMPONENT_MODULE_SOURCE]: 'standard module',
                [trackingConstants.COMPONENT_MODULE_NAME]: 'Marquee',
                [trackingConstants.COMPONENT_MODULE_CUSTOM_NAME]: tile.trackingDetail?.moduleCustomName ?? NOT_APPLICABLE,
                [trackingConstants.COMPONENT_MODULE_POSITION]: tile.trackingDetail?.modulePosition ?? NOT_APPLICABLE,
                [trackingConstants.COMPONENT_PAGE_LAYOUT]: tile.trackingDetail?.pageLayout ?? NOT_APPLICABLE,
                [trackingConstants.MARQUEE_RECOMMENDATION_TYPE]: tile.trackingDetail?.marqueeRecommendationType ?? NOT_APPLICABLE,
            };
        });

        this.trackingService.track(trackingConstants.SHOW_MARQUEE, {
            [trackingConstants.SHOW_MARQUEE]: marqueeTrackingData,
        });
    }

    trackOutrightClicks(index: number, globalLink: string, sportId: number, competitionId: number, backgroundImage?: string): void {
        const contentPosition = isNaN(index) ? NOT_APPLICABLE : index.toString();

        const trackingObject = {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: 'Marquee',
            [trackingConstants.COMPONENT_LABEL_EVENT]: `OutrightMarquee`,
            [trackingConstants.COMPONENT_ACTION_EVENT]: 'Marquee - Clicked',
            [trackingConstants.COMPONENT_POSITION_EVENT]: NOT_APPLICABLE,
            [trackingConstants.COMPONENT_CONTENT_POSITION]: contentPosition,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: this.getEventDetails(backgroundImage),
            [trackingConstants.COMPONENT_URL_CLICKED]: globalLink,
            [trackingConstants.SPORT_ID]: sportId.toString(),
            [trackingConstants.SPORT_LEAGUE_ID]: competitionId.toString(),
        };

        this.trackingService.track(trackingConstants.EVENT_CLICKS, trackingObject);
    }

    trackLinkClick(globalLink?: string): void {
        const trackingObject = {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: 'Marquee',
            [trackingConstants.COMPONENT_LABEL_EVENT]: `Marquee Container`,
            [trackingConstants.COMPONENT_ACTION_EVENT]: 'Header - Clicked',
            [trackingConstants.COMPONENT_EVENT_DETAILS]: 'Global Link',
            [trackingConstants.COMPONENT_URL_CLICKED]: globalLink,
            [trackingConstants.COMPONENT_POSITION_EVENT]: NOT_APPLICABLE,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: NOT_APPLICABLE,
        };
        this.trackingService.track(trackingConstants.EVENT_TRACKING, trackingObject);
    }

    trackBetBuilderCard(): void {
        this.trackingService.update({ [trackingConstants.PAGE_REFERRING_ACTION]: 'BuildABet_CouponPage_Link_Click' });
    }

    private getLabelEvent(marqueeType: MarqueeType): string {
        switch (marqueeType) {
            case MarqueeType.Regular:
                return TrackingTileNames.RegularMarquee;
            case MarqueeType.BuildABet:
                return TrackingTileNames.BuildABetMarquee;
            case MarqueeType.Special:
                return TrackingTileNames.SpecialMarquee;
        }
    }

    private getEventDetails(imageUrl?: string, isCardAreaClicked: boolean = true, trackData?: string): string {
        if (isCardAreaClicked) {
            return imageUrl ? 'Image Promoted' : (trackData ?? 'No Image Promoted');
        }

        return 'No Image Promoted';
    }

    trackGoToLink(tile: FixtureTile, page: HighlightsMarqueePage, url: string, eventLinkText: string): void {
        const trackingObject = {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: 'links',
            [trackingConstants.COMPONENT_LABEL_EVENT]: 'marquee',
            [trackingConstants.COMPONENT_ACTION_EVENT]: 'click',
            [trackingConstants.COMPONENT_EVENT_DETAILS]: eventLinkText,
            [trackingConstants.COMPONENT_URL_CLICKED]: url,
            [trackingConstants.COMPONENT_POSITION_EVENT]: `${tile.racingEvent?.sport.name.value}`,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.highlightsMarqueePageService.mapPageSource(page),
            [trackingConstants.COMPONENT_MODULE_TYPE]: NOT_APPLICABLE,
        };
        this.trackingService.track(trackingConstants.EVENT_TRACKING, trackingObject);
    }
}
